import React, { useEffect, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import { Shield } from 'lucide-react';
import { trustElements, licenseImage } from '../data/mock';

// Lottie animation for folder/document
const folderAnimation = {
  "v": "5.5.7",
  "fr": 30,
  "ip": 0,
  "op": 60,
  "w": 100,
  "h": 100,
  "layers": [{
    "ty": 4,
    "nm": "folder",
    "sr": 1,
    "ks": {
      "o": { "a": 0, "k": 100 },
      "r": { "a": 0, "k": 0 },
      "p": { "a": 0, "k": [50, 50, 0] },
      "a": { "a": 0, "k": [0, 0, 0] },
      "s": { "a": 1, "k": [{ "t": 0, "s": [100, 100], "e": [105, 105] }, { "t": 30, "s": [105, 105], "e": [100, 100] }, { "t": 60, "s": [100, 100] }] }
    },
    "shapes": [{
      "ty": "gr",
      "it": [
        { "ty": "rc", "s": { "a": 0, "k": [40, 30] }, "p": { "a": 0, "k": [0, 5] }, "r": { "a": 0, "k": 3 } },
        { "ty": "fl", "c": { "a": 0, "k": [0.3, 0.4, 0.5, 1] } },
        { "ty": "tr", "p": { "a": 0, "k": [0, 0] } }
      ]
    }, {
      "ty": "gr",
      "it": [
        { "ty": "rc", "s": { "a": 0, "k": [15, 5] }, "p": { "a": 0, "k": [-8, -12] }, "r": { "a": 0, "k": 2 } },
        { "ty": "fl", "c": { "a": 0, "k": [0.3, 0.4, 0.5, 1] } },
        { "ty": "tr", "p": { "a": 0, "k": [0, 0] } }
      ]
    }],
    "ip": 0,
    "op": 60
  }]
};

const TrustSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-slate-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Stats with counter animation */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20">
          {trustElements.map((item, index) => (
            <div 
              key={item.id} 
              className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2">
                {item.value}
              </div>
              <div className="text-slate-200 text-base md:text-lg font-semibold">{item.label}</div>
              <div className="text-slate-400 text-sm md:text-base">{item.description}</div>
            </div>
          ))}
        </div>

        {/* License Section with slide-in */}
        <div 
          className={`bg-slate-800/80 border border-slate-700/50 rounded-2xl p-8 md:p-14 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          style={{ transitionDelay: '400ms' }}
        >
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Text */}
            <div>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14">
                  <Lottie animationData={folderAnimation} loop={true} />
                </div>
                <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-5 py-2.5">
                  <Shield className="w-5 h-5 text-green-400" />
                  <span className="text-green-400 text-base font-semibold">Agenție Autorizată</span>
                </div>
              </div>
              
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Detectivi Bacau — Licență Oficială IGPR
              </h3>
              
              <p className="text-slate-300 text-lg leading-relaxed mb-8">
                Suntem o agenție de detectivi particulari din Bacau pe deplin autorizată să funcționeze 
                conform legislației române. Licența noastră garantează că toate investigațiile efectuate de detectivii privați din Bacau 
                sunt legale, iar dovezile obținute sunt admisibile în instanță.
              </p>

              <div className="grid grid-cols-2 gap-5">
                {['Licență IGPR', 'Conformitate GDPR', 'Dovezi legale', 'Confidențialitate'].map((item, i) => (
                  <div 
                    key={i} 
                    className={`flex items-center gap-3 transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                    style={{ transitionDelay: `${600 + i * 100}ms` }}
                  >
                    <div className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-slate-200 text-base font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* License Image with hover effect */}
            <div 
              className={`flex justify-center transition-all duration-1000 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
              style={{ transitionDelay: '600ms' }}
            >
              <div className="w-48 h-48 md:w-56 md:h-56 rounded-xl overflow-hidden bg-white p-3 border-2 border-slate-600/50 shadow-xl
                hover:scale-105 hover:shadow-2xl hover:shadow-slate-400/20 transition-all duration-500 group">
                <img 
                  src={licenseImage} 
                  alt="Licență Detectiv Particular Bacau IGPR - Autorizație oficială detectiv privat Bacau pentru servicii de investigații private în județul Bacău" 
                  className="w-full h-full object-contain rounded group-hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
